/**
 * The board's header: title and range, date navigation, and the control the
 * active screen needs (View 1 switches sub-view and day layout; View 2
 * switches shift; View 3 has no control).
 *
 * Date navigation lives here because the board IS the page in this mode —
 * without it the roster is stuck on whichever week it loaded and no other
 * week's plan is reachable. Steps by week in the Week sub-view, by day
 * everywhere else.
 */
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PrintIcon from "@mui/icons-material/Print";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import React, { useCallback } from "react";

import { BoardScreen, BoardView } from "../types";
import { SHIFT_BLOCKS, SHIFT_LABEL, ShiftNumber } from "../utils/gap-derive";
import { lbb } from "../utils/tokens";

const stepStyle: React.CSSProperties = {
	fontFamily: lbb.fontUi,
	fontSize: 15,
	fontWeight: 700,
	lineHeight: 1,
	padding: "8px 12px",
	border: lbb.borderInk,
	borderRadius: 4,
	background: lbb.paper,
	color: lbb.navy,
	cursor: "pointer",
};

const todayStyle: React.CSSProperties = {
	...stepStyle,
	fontSize: 13,
	padding: "9px 14px",
};

const tabStyle = (on: boolean): React.CSSProperties => ({
	fontFamily: lbb.fontUi,
	fontSize: 13,
	fontWeight: on ? 700 : 600,
	padding: "7px 16px",
	cursor: "pointer",
	border: "none",
	color: on ? lbb.paper : lbb.navy,
	background: on ? lbb.navy : "transparent",
});

/** One segment of a segmented control; reports its own value when clicked. */
function Tab<T extends string | number>({
	value,
	on,
	label,
	onPick,
	style,
}: {
	value: T;
	on: boolean;
	label: string;
	onPick: (value: T) => void;
	style?: React.CSSProperties;
}) {
	const handleClick = useCallback(() => onPick(value), [onPick, value]);
	return (
		<button type="button" onClick={handleClick} style={{ ...tabStyle(on), ...style }}>
			{label}
		</button>
	);
}

const VIEWS: BoardView[] = ["Week", "Day"];
const SHIFTS: ShiftNumber[] = [1, 2];

export interface BoardHeaderProps {
	rangeLabel: string;
	screen: BoardScreen;
	view: BoardView;
	shift: ShiftNumber;
	/** Steps by week in the Week sub-view, by day everywhere else. */
	isWeek: boolean;
	onStep: (direction: -1 | 1) => void;
	onToday: () => void;
	onView: (view: BoardView) => void;
	onShift: (shift: ShiftNumber) => void;
	/** Prints the week by line; offered in the Week sub-view only. */
	onPrint?: () => void;
	/** Downloads the shown week's roster (who is on which job order) as a spreadsheet. */
	onDownload?: () => void;
	/** Opens the floor display (the TV screen: who works where right now) in a new tab. */
	onDisplay?: () => void;
	/** Empties the shown day or week so the planner can place people by hand. */
	onClear?: () => void;
	/** Fill the open slots — here when the board has no KPI row to carry the button (Shift Roster). */
	onFill?: () => void;
	fillLabel?: string;
	filling?: boolean;
	/** Opens "Fill a range of days" (many days, on the server). */
	onFillRange?: () => void;
	/** "Filling 34/120" while a range fill runs. */
	fillRangeLabel?: string;
}

const BoardHeader: React.FC<BoardHeaderProps> = ({
	rangeLabel,
	screen,
	view,
	shift,
	isWeek,
	onStep,
	onToday,
	onView,
	onShift,
	onPrint,
	onDownload,
	onDisplay,
	onClear,
	onFill,
	fillLabel,
	filling,
	onFillRange,
	fillRangeLabel,
}) => {
	const handlePrevious = useCallback(() => onStep(-1), [onStep]);
	const handleNext = useCallback(() => onStep(1), [onStep]);
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				padding: "18px 24px",
				borderBottom: lbb.borderInk,
			}}
		>
			<div style={{ display: "flex", alignItems: "baseline", gap: 16, minWidth: 0 }}>
				<div
					style={{
						fontFamily: lbb.fontBrand,
						fontSize: 26,
						fontWeight: 700,
						letterSpacing: "-0.02em",
						color: lbb.navy,
					}}
				>
					Roster
				</div>
				<div
					style={{
						fontFamily: lbb.fontMono,
						fontSize: 13,
						letterSpacing: "0.04em",
						color: lbb.ink60,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{rangeLabel}
				</div>
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
				<button
					type="button"
					onClick={handlePrevious}
					aria-label={isWeek ? "Previous week" : "Previous day"}
					style={stepStyle}
				>
					‹
				</button>
				<button type="button" onClick={onToday} style={todayStyle}>
					Today
				</button>
				<button type="button" onClick={handleNext} aria-label={isWeek ? "Next week" : "Next day"} style={stepStyle}>
					›
				</button>
			</div>

			{screen === "util" && (
				<div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
					<div
						style={{
							display: "flex",
							border: lbb.borderInk,
							borderRadius: 4,
							overflow: "hidden",
						}}
					>
						{VIEWS.map((v) => (
							<Tab key={v} value={v} on={view === v} label={v} onPick={onView} />
						))}
					</div>
					{view === "Week" && onPrint && (
						<button
							type="button"
							onClick={onPrint}
							title="Print this week's roster by line"
							aria-label="Print week"
							style={{ ...todayStyle, display: "flex", alignItems: "center", gap: 6 }}
						>
							<PrintIcon sx={{ fontSize: 16 }} />
							Print
						</button>
					)}
					{onDownload && (
						<button
							type="button"
							onClick={onDownload}
							title="Download this week's roster as a spreadsheet: every person, job order and hours"
							style={{ ...todayStyle, display: "flex", alignItems: "center", gap: 6 }}
						>
							<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
							Download
						</button>
					)}
					{onDisplay && (
						<button
							type="button"
							onClick={onDisplay}
							title="Open the floor display for a TV: who works on which line right now"
							style={{ ...todayStyle, display: "flex", alignItems: "center", gap: 6 }}
						>
							<TvOutlinedIcon sx={{ fontSize: 16 }} />
							TV display
						</button>
					)}
					{onClear && (
						<button
							type="button"
							onClick={onClear}
							title={isWeek ? "Clear this week's roster and place people by hand" : "Clear today's roster and place people by hand"}
							style={{ ...todayStyle, color: lbb.brick, borderColor: lbb.brick }}
						>
							{isWeek ? "Clear week" : "Clear day"}
						</button>
					)}
					{onFill && (
						<button
							type="button"
							onClick={onFill}
							disabled={filling}
							style={{ ...todayStyle, background: filling ? lbb.brickDeep : lbb.brick, borderColor: lbb.brick, color: lbb.paper, cursor: filling ? "progress" : "pointer" }}
						>
							{fillLabel ?? "Fill open slots"}
						</button>
					)}
					{onFillRange && (
						<button type="button" onClick={onFillRange} title="Fill many days at once, on the server" style={{ ...todayStyle, color: lbb.brick, borderColor: lbb.brick }}>
							{fillRangeLabel ?? "Fill range…"}
						</button>
					)}
				</div>
			)}
			{screen === "gaps" && (
				<div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
					<span style={{ fontFamily: lbb.fontMono, fontSize: 13, color: lbb.ink60 }}>
						{SHIFT_BLOCKS[shift][0].label.split(" – ")[0]} –{" "}
						{SHIFT_BLOCKS[shift][1].label.split(" – ")[1]}
					</span>
					<div style={{ display: "flex", border: lbb.borderInk, borderRadius: 4, overflow: "hidden" }}>
						{SHIFTS.map((s2) => (
							<Tab key={s2} value={s2} on={shift === s2} label={SHIFT_LABEL[s2]} onPick={onShift} />
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default BoardHeader;
